/**
 * 非同期ジョブの縦ステッパー (docs/features/manual-product-brand-first-flow.md §8)。
 *
 * 長い処理 (URL 読み取り・ブランドのサイトからの提案) の間、全体にどういう段階があり、いまどこかを見せて待ち時間の
 * 不安を減らす。段階の状態は backend が進捗応答の stages ([{key, status}]) で返す (完了 / 実行中 / 未実行 / スキップ /
 * 失敗 / 中止)。FE は観測に頼らない (ポーリングの間に終わった短い段階も backend が done にする)。
 * - スキップされた段階は一覧から消す (「やらなかったこと」を完了扱いにしない)
 * - 現在の段階の下に細かい進捗 (step) と経過時間・注意文を出す
 * - 段階に入れ子の中身 (読み込み予定ページの一覧など) を出したいときは `children` に段階キー → 要素を渡す
 * 処理が終わったら呼び出し側が外す (結果の表示に切り替える)。
 */
import { Check, Loader2, X } from 'lucide-react';
import type { ReactNode } from 'react';
import type { JobStageState } from '../../features/brands/siteProposal';
import './JobStepper.css';

interface Props {
  stages: JobStageState[];
  /** 段階キー → 一覧用のラベル (体言止め) */
  labels: Record<string, string>;
  /** 現在の段階の細かい進捗 (一文) */
  stepText?: string | null;
  /** 経過時間の表示 (例: "1分20秒") */
  elapsed?: string | null;
  /** 遅延時の注意など */
  notice?: string | null;
  /** 現在行に添える補足 (例: "3/5 ページ")。段階キー → 文字列 */
  detail?: Record<string, string | undefined>;
  /** 段階キー → その行の直下に入れ子で出す要素 */
  children?: Record<string, ReactNode>;
  ariaLabel?: string;
}

export function JobStepper({ stages, labels, stepText, elapsed, notice, detail, children, ariaLabel = '処理の段階' }: Props) {
  const visible = stages.filter((s) => s.status !== 'skipped');
  return (
    <ol className="job-stepper" aria-label={ariaLabel} data-testid="job-stepper">
      {visible.map((s) => {
        const label = s.label ?? labels[s.key] ?? s.key;
        const active = s.status === 'running';
        const stopped = s.status === 'failed' || s.status === 'cancelled';
        return (
          <li
            key={s.key}
            className={`job-stepper-row is-${s.status}`}
            aria-current={active ? 'step' : undefined}
            data-testid={`job-stepper-${s.key}`}
          >
            <span className="job-stepper-marker" aria-hidden="true">
              {s.status === 'done' && <Check size={12} />}
              {active && <Loader2 size={12} className="job-stepper-spinner" />}
              {stopped && <X size={12} />}
            </span>
            <div className="job-stepper-body">
              <span className="job-stepper-label">
                {label}
                {detail?.[s.key] && <span className="job-stepper-detail"> ({detail[s.key]})</span>}
              </span>
              {active && (stepText || elapsed || notice) && (
                <span className="job-stepper-step" role="status">
                  {stepText && <span>{stepText}</span>}
                  {elapsed && <span className="job-stepper-elapsed">{elapsed}経過</span>}
                  {notice && <span className="job-stepper-notice">{notice}</span>}
                </span>
              )}
              {stopped && notice && <span className="job-stepper-step job-stepper-stopped">{notice}</span>}
              {children?.[s.key] && <div className="job-stepper-nested">{children[s.key]}</div>}
            </div>
          </li>
        );
      })}
    </ol>
  );
}
